// =============================================================================
// useMyProjectIds.ts — T-245 (Phase 1 of T-183), rebuilt for migration 333
//
// Shared "what projects am I currently on" hook. Two legs, mirroring
// app.is_on_project() exactly:
//
//   1. Direct individual assignments via project_team
//   2. Roster stints via site_roster (333) — seeded when a crew is booked,
//      owned per person, includes future stints and "listed" standbys.
//
// The old second leg was crew_members → project_crew_assignments: an INFERENCE
// from today's membership, which over-granted (every member saw every crew
// job whether rostered or not) and under-granted (membership edited → access
// gone, even for sites they genuinely worked). The roster is the record, so
// access now follows the person's actual stints.
//
// Consumers (FieldMobileHome, MyWeekScreen, FieldProjectsListScreen) all need
// the same authoritative set of project_ids the user can act on.
// =============================================================================

import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { CoretelUser } from "@/lib/auth";

export interface UseMyProjectIdsResult {
  projectIds: string[];
  isLoading: boolean;
  error: unknown;
}

export function useMyProjectIds(user: CoretelUser | null): UseMyProjectIdsResult {
  const q = useQuery({
    queryKey: ["my_project_ids", user?.employee_id],
    enabled: !!user?.employee_id,
    queryFn: async (): Promise<string[]> => {
      // 14-DAY TAIL - mirrors app.is_on_project() exactly (migrations 327/333).
      // A stint that ended keeps its projects for a fortnight, so a tech who
      // came off site on Friday can still upload Friday's HOP photos on Monday
      // and answer the client's call on Wednesday. Before this, closing a crew
      // membership - a move, a roster tidy-up - cut access the same day with
      // no error, and the tech saw "No projects assigned yet."
      //
      // If you change the window, change it in migration 333 too: this hook
      // decides what the UI LISTS, is_on_project() decides what RLS SERVES,
      // and they must agree or the list shows projects whose tabs then 404.
      // mig 495 — the tail is per PROJECT now (projects.access_tail_days, default
      // 14, up to 60). Fetch at the widest, then keep a row only if it is inside
      // its own project's tail - the same test app.is_on_project runs.
      // mig 496 — four legs, same as app.is_on_project: project_team, the
      // roster (a seeded stint with no end follows its BOOKING's end), an
      // ad-hoc task on the project, and (not here — a tablet has no employee)
      // the crew tablet. Local day keys, never toISOString (AEST shifts the day).
      const MAX_TAIL = 60;
      const localKey = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      const cutoffDate = new Date(); cutoffDate.setDate(cutoffDate.getDate() - MAX_TAIL);
      const cutoff = localKey(cutoffDate);
      const todayMs = new Date(localKey(new Date()) + "T00:00:00").getTime();

      // Both legs in parallel. Roster: open stints (null = follows the
      // booking) or ended within the tail. Future stints and "listed"
      // standbys qualify - being on the list for an upcoming job is exactly
      // when you need the docs to get your head around it (James, 27 Aug).
      const [
        { data: teamRows, error: teamErr },
        { data: srRows,   error: srErr },
        { data: pbRows,   error: pbErr },
      ] = await Promise.all([
        supabase
          .from("project_team")
          .select("project_id, assigned_to")
          .eq("employee_id", user!.employee_id!)
          .or(`assigned_to.is.null,assigned_to.gte.${cutoff}`),
        (supabase as any)
          .from("site_roster")
          .select("project_id, on_site_to, booking:pca_id ( assigned_to )")
          .eq("employee_id", user!.employee_id!),
        (supabase as any)
          .from("personal_bookings")
          .select("project_id, scheduled_start, scheduled_end")
          .eq("assignee_employee_id", user!.employee_id!)
          .not("project_id", "is", null)
          .gte("scheduled_start", new Date(Date.now() - (MAX_TAIL + 30) * 86_400_000).toISOString()),
      ]);
      if (teamErr) throw teamErr;
      if (srErr)   throw srErr;
      if (pbErr)   throw pbErr;

      const candidate = new Set<string>();
      for (const t of teamRows ?? []) if (t.project_id) candidate.add(t.project_id);
      for (const r of (srRows ?? []) as { project_id: string | null }[]) if (r.project_id) candidate.add(r.project_id);
      for (const b of (pbRows ?? []) as { project_id: string | null }[]) if (b.project_id) candidate.add(b.project_id);
      const tails = new Map<string, number>();
      if (candidate.size) {
        const { data: projs } = await (supabase as any).from("projects").select("project_id, access_tail_days").in("project_id", [...candidate]);
        for (const p of (projs ?? []) as { project_id: string; access_tail_days: number | null }[]) tails.set(p.project_id, Number(p.access_tail_days ?? 14));
      }
      const inside = (projectId: string, endKey: string | null) => {
        if (!endKey) return true;
        const tail = tails.get(projectId) ?? 14;
        return new Date(endKey + "T00:00:00").getTime() >= todayMs - tail * 86_400_000;
      };
      const ids = new Set<string>();
      for (const t of teamRows ?? []) if (t.project_id && inside(t.project_id, (t as any).assigned_to ?? null)) ids.add(t.project_id);
      for (const r of (srRows ?? []) as { project_id: string | null; on_site_to: string | null; booking: { assigned_to: string | null } | null }[]) {
        if (r.project_id && inside(r.project_id, r.on_site_to ?? r.booking?.assigned_to ?? null)) ids.add(r.project_id);
      }
      for (const b of (pbRows ?? []) as { project_id: string | null; scheduled_start: string; scheduled_end: string | null }[]) {
        if (b.project_id && inside(b.project_id, (b.scheduled_end ?? b.scheduled_start).slice(0, 10))) ids.add(b.project_id);
      }

      return Array.from(ids);
    },
  });

  return {
    projectIds: q.data ?? [],
    isLoading:  q.isLoading,
    error:      q.error,
  };
}
